import { useEffect, useState } from "react";
import { useMutation } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import Banner from "@/components/Banner";
import Button from "@/components/Button";
import DateInput from "@/components/DateInput";
import Dialog from "@/components/Dialog";
import { CREATE_TASK_MUTATION, type CreateTaskResponse } from "@/features/global/components/AddTaskSideSheet/mutations";
import { evictTasksCache } from "@/pages/tasks/queries";
import {
  buildEmploymentCareerPageUrl,
  buildEmploymentChangeNoticeTaskInput,
  employmentChangeNoticePersonName,
  MEMBERSHIP_SERVICES_DEPARTMENT,
  type EmploymentChangeNoticePerson
} from "./employmentChangeNotice";
import useAmplitude from "@/hooks/useAmplitude";
import { dateOnlyToJsDate } from "@/lib/dateHelpers";
import { showErrorSnackbar, showSnackbar } from "@/lib/snackbarUtils";

export type NotifyEmploymentChangePerson = EmploymentChangeNoticePerson;

export type NotifyEmploymentChangeSource = "person_employment" | "facility_employees";

type NotifyEmploymentChangeModalProps = {
  open: boolean;
  onClosed: () => void;
  pgaId: string;
  personId: string;
  person: NotifyEmploymentChangePerson;
  facilityId: string;
  facilityName: string;
  startDateRaw?: string | null;
  source?: NotifyEmploymentChangeSource;
};

export default function NotifyEmploymentChangeModal({
  open,
  onClosed,
  pgaId,
  personId,
  person,
  facilityId,
  facilityName,
  startDateRaw,
  source = "person_employment"
}: NotifyEmploymentChangeModalProps) {
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const [endedOn, setEndedOn] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [createTask, { loading: creating }] = useMutation<CreateTaskResponse>(CREATE_TASK_MUTATION, {
    update: evictTasksCache
  });

  const minDate = dateOnlyToJsDate(startDateRaw);

  useEffect(() => {
    if (!open) return;
    setEndedOn(null);
    setMutationError(null);
  }, [open]);

  const handleCreateTask = async () => {
    if (!endedOn) return;

    setMutationError(null);
    const personName = employmentChangeNoticePersonName(person);
    const input = buildEmploymentChangeNoticeTaskInput({
      personName,
      pgaId,
      personId,
      facilityId,
      facilityName,
      endedOn,
      careerPageUrl: buildEmploymentCareerPageUrl(pgaId)
    });

    try {
      const result = await createTask({ variables: { input } });
      if (!result.data?.createTask?.success) {
        setMutationError(result.data?.createTask?.message || "Failed to create task");
        return;
      }

      const taskId = result.data.createTask.task?.id;
      trackEvent("employment change notice sent", {
        destination: "membership_services_task",
        source
      });
      showSnackbar("Membership Services task created", {
        actions: taskId
          ? [
              <Button key="view" variant="text" onClick={() => navigate(`/tasks/${taskId}`)}>
                View Task
              </Button>
            ]
          : undefined
      });
      onClosed();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Error creating task. Please try again.";
      setMutationError(errorMessage);
      showErrorSnackbar(errorMessage);
    }
  };

  return (
    <Dialog
      open={open}
      onClosed={onClosed}
      headline="Notify of Employment Change"
      hasFormElements
      displayOverflow
      disableClose={creating}
      actions={
        <>
          <Button variant="text" onClick={onClosed} disabled={creating}>
            Cancel
          </Button>
          <Button variant="filled" onClick={handleCreateTask} disabled={!endedOn} loading={creating}>
            Create Task
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-6">
        {mutationError ? (
          <Banner variant="error" description={mutationError} inline closable={false} hideSecondaryDismiss />
        ) : null}
        <p className="text-body-medium text-sys-brand-on-surface m-0">
          Choose the employment end date. This creates a {MEMBERSHIP_SERVICES_DEPARTMENT} department task with the
          person, facility, end date, and a link to this career page.
        </p>
        <DateInput label="End Date *" value={endedOn} onChange={setEndedOn} required useModal minDate={minDate} />
      </div>
    </Dialog>
  );
}
